import React, { useState } from "react";
import { selectProjectLoading, selectProjectInitialized } from "./ProjectSlice_Store";
import { uploadFileToProject } from "./services/fileUploadService";
import { useAppSelector } from "./hooks/reduxHooks";

export default function UploadPage_Component(): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Get current user info from Redux auth state only
  const reduxAuth = useAppSelector(state => state.userAuthAndAdminSlice);

  // Get current project ID and loading state from Redux project state
  const { projectId } = useAppSelector(state => state.projectSlice);
  const projectLoading = useAppSelector(selectProjectLoading);
  const _projectInitialized = useAppSelector(selectProjectInitialized);

  // Get current user ID from Redux state (supports both MSAL and custom auth)
  const getCurrentUserId = (): string => {
    if (reduxAuth?.isAuthenticated && reduxAuth?.userId) {
      return reduxAuth.userId as string;
    }
    if (reduxAuth?.isAuthenticated && reduxAuth?.userEmail) {
      return reduxAuth.userEmail as string;
    }
    return "test.user"; // Fallback
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError("");
      setUploadStatus("");
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    // Ensure we have a valid project ID before attempting upload
    if (!projectId) {
      setError("No project available. Please wait for project to be initialized and try again.");
      return;
    }

    setUploading(true);
    setError("");
    setUploadStatus("Getting upload URL...");

    try {
      const { filePath } = await uploadFileToProject({
        file,
        projectId: projectId,
        username: getCurrentUserId(),
        onStatusChange: (status: string) => {
          if (status === 'fetching_sas') {
            setUploadStatus("Getting upload URL...");
          }
          if (status === 'uploading') {
            setUploadStatus("Uploading file...");
          }
        }
      });

      setUploadStatus(`File uploaded successfully to: ${filePath}`);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById("file-input") as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";

    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploadStatus("");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            File Upload Test
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Test the file-access-service upload functionality
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="mt-4">
                <label htmlFor="file-input" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    {file ? file.name : "Choose a file to upload"}
                  </span>
                  <input
                    id="file-input"
                    name="file-input"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Any file type supported
                </p>
              </div>
            </div>
          </div>

          {file && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Selected File</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p><strong>Name:</strong> {file.name}</p>
                    <p><strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB</p>
                    <p><strong>Type:</strong> {file.type || "Unknown"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading || !projectId || projectLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : projectLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Initializing project...
                </>
              ) : !projectId ? (
                "Waiting for project..."
              ) : (
                "Upload File"
              )}
            </button>
          </div>

          {uploadStatus && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    {uploadStatus}
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Files are uploaded to: originals/{projectId || "waiting-for-project"}/{"{filename}"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Logged in as: {getCurrentUserId()} | Project: {
                projectLoading ? "Loading..." :
                projectId ? `${projectId.substring(0, 8)}...` :
                "Waiting for initialization"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
